import React, { useRef, useState, useEffect, TouchEvent, MouseEvent } from 'react';
import { Doctor } from '../types/api';
import { apiService } from '../services/api';
import { mapStoredUserToDoctor, parseStoredUser } from '../utils/doctorUser';

interface SignaturePageProps {
  doctor: Doctor | null;
  onSave: () => void;
}

function resolveDoctor(prop: Doctor | null): Doctor | null {
  if (prop) return prop;
  const u = parseStoredUser();
  return u ? mapStoredUserToDoctor(u) : null;
}

function SignaturePage({ doctor, onSave }: SignaturePageProps) {
  const [effectiveDoctor, setEffectiveDoctor] = useState<Doctor | null>(() => resolveDoctor(doctor));

  useEffect(() => {
    setEffectiveDoctor(resolveDoctor(doctor));
  }, [doctor]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef<boolean>(false);
  const drawingPointsRef = useRef<number>(0);
  const totalPathLengthRef = useRef<number>(0);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<{ show: boolean; message: string; type?: 'info' | 'success' | 'error' }>({ show: false, message: '', type: 'info' });
  const [hasDrawn, setHasDrawn] = useState<boolean>(false);
  const [isSignatureValid, setIsSignatureValid] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Check token on mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    console.log('SignaturePage mounted - Token exists:', !!token);
    console.log('SignaturePage mounted - User data:', user);
    if (!token) {
      setSaveError('No authentication token found. Please login again.');
    }
  }, []);

  const validateSignature = () => {
    // Similar to CheckLine: require minimum drawing points (writingCount >= 30)
    // Also check minimum path length to ensure signature covers enough area
    const minPoints = 30;
    const minPathLength = 200; // pixels
    
    const isValid = drawingPointsRef.current >= minPoints && totalPathLengthRef.current >= minPathLength;
    setIsSignatureValid(isValid);
    return isValid;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      // Only resize if dimensions actually changed
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        const imageData = ctx.getImageData(0, 0, canvas.width / dpr, canvas.height / dpr);
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, rect.height);
        // Restore previous drawing if it exists
        if (imageData) {
          ctx.putImageData(imageData, 0, 0);
        }
      }
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Handle touch events with native listeners (non-passive) to prevent scrolling
    const getTouchPos = (e: globalThis.TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0] || e.changedTouches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    };
    
    let lastTouchPos: { x: number; y: number } | null = null;
    
    const handleTouchStart = (e: globalThis.TouchEvent) => {
      e.preventDefault();
      const pos = getTouchPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      isDrawingRef.current = true;
      setIsDrawing(true);
      drawingPointsRef.current++;
      lastTouchPos = pos;
    };
    
    const handleTouchMove = (e: globalThis.TouchEvent) => {
      e.preventDefault();
      if (!isDrawingRef.current) return;
      const pos = getTouchPos(e);
      
      if (lastTouchPos) {
        const dx = pos.x - lastTouchPos.x;
        const dy = pos.y - lastTouchPos.y;
        totalPathLengthRef.current += Math.sqrt(dx * dx + dy * dy);
      }
      lastTouchPos = pos;
      
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      drawingPointsRef.current++;
      setHasDrawn(true);
      validateSignature();
    };
    
    const handleTouchEnd = () => {
      isDrawingRef.current = false;
      setIsDrawing(false);
    };
    
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  const getPos = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    drawingPointsRef.current++;
  };

  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  const draw = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const pos = getPos(e);
    
    if (lastPosRef.current) {
      const dx = pos.x - lastPosRef.current.x;
      const dy = pos.y - lastPosRef.current.y;
      totalPathLengthRef.current += Math.sqrt(dx * dx + dy * dy);
    }
    lastPosRef.current = pos;
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    drawingPointsRef.current++;
    setHasDrawn(true);
    validateSignature();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
    setIsSignatureValid(false);
    drawingPointsRef.current = 0;
    totalPathLengthRef.current = 0;
    lastPosRef.current = null;
  };

  const handleSave = async () => {
    if (!hasDrawn) {
      setAlertModal({ show: true, message: 'Please sign before saving', type: 'error' });
      return;
    }
    
    // Validate signature is sufficient (similar to CheckLine validation)
    if (!validateSignature()) {
      setAlertModal({ show: true, message: 'Signature Insufficient\n\nPlease sign again. Your signature must be more complete.', type: 'error' });
      return;
    }
    
    if (!effectiveDoctor?.id) {
      setAlertModal({ show: true, message: 'Doctor information is missing', type: 'error' });
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) {
      setAlertModal({ show: true, message: 'Canvas not found', type: 'error' });
      return;
    }
    
    try {
      setIsSaving(true);
      setSaveError(null);
      
      // Check if token exists
      const token = localStorage.getItem('accessToken');
      const user = localStorage.getItem('user');
      
      console.log('=== Signature Save Debug ===');
      console.log('Token exists:', !!token);
      console.log('Token value:', token ? token.substring(0, 50) + '...' : 'null');
      console.log('User data:', user);
      console.log('Doctor ID:', effectiveDoctor.id);
      
      if (!token) {
        setSaveError('No authentication token found. Please login again.');
        setIsSaving(false);
        return;
      }
      
      // Decode token to check claims (for debugging)
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('Token payload:', payload);
          console.log('Token sub claim:', payload.sub);
          console.log('Token role_id claim:', payload.role_id);
          console.log('Token expires:', new Date(payload.exp * 1000));
        }
      } catch (e) {
        console.warn('Could not decode token:', e);
      }
      
      console.log('Saving signature for doctor ID:', effectiveDoctor.id);
      
      // Convert canvas to PNG base64
      const base64Image = canvas.toDataURL('image/png');
      // Remove data URL prefix (data:image/png;base64,)
      const base64Data = base64Image.split(',')[1];
      
      console.log('Base64 data length:', base64Data.length);
      
      // Save signature via API
      const response = await apiService.saveSignature({
        empID: effectiveDoctor.id,
        multiUse: true,
        signatureData: base64Data
      });
      console.log('Signature save response:', response);
      
      if (response.hasSignature) {
        // Success - navigate to queue
        onSave();
      } else {
        setSaveError('Failed to save signature. Please try again.');
      }
    } catch (err: any) {
      console.error('Error saving signature:', err);
      let errorMessage = 'Failed to save signature';
      
      if (err.response) {
        // Server responded with error
        if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data?.errors) {
          // Handle validation errors
          const errors = err.response.data.errors;
          const errorMessages = Object.values(errors).flat().join(', ');
          errorMessage = errorMessages || `Server error: ${err.response.status}`;
        } else {
          errorMessage = `Server error: ${err.response.status} - ${err.response.statusText}`;
        }
        
        // Handle 401 specifically
        if (err.response.status === 401) {
          const token = localStorage.getItem('accessToken');
          if (!token) {
            errorMessage = 'No authentication token found. Please login again.';
          } else {
            errorMessage = err.response.data?.message || 'Authentication failed. Your session may have expired. Please login again.';
          }
          // Don't auto-redirect, let user see the error
          console.error('401 Unauthorized - Token exists:', !!token, 'Response:', err.response.data);
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
        console.error('Request details:', err.request);
      } else {
        // Something else happened
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="signature-container">
      <div className="signature-box">
        <h1 className="login-title">Signature Required</h1>
        <p className="login-subtitle">
          Welcome, {effectiveDoctor?.name}. Please sign below.
          Your signature will be used for Medical Certificates.
        </p>
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
        <div className="signature-actions">
          <button className="btn btn-secondary" onClick={clearCanvas} disabled={isSaving}>Clear</button>
          <button 
            className={`btn btn-primary ${!isSignatureValid && hasDrawn ? 'btn-warning' : ''}`} 
            onClick={handleSave}
            disabled={!hasDrawn || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Signature'}
          </button>
        </div>
        {saveError && (
          <div style={{ 
            marginTop: '12px', 
            padding: '8px 12px', 
            background: '#fee2e2', 
            border: '1px solid #dc2626', 
            borderRadius: '6px',
            color: '#991b1b',
            fontSize: '13px',
            textAlign: 'center'
          }}>
            ⚠️ {saveError}
          </div>
        )}
        {hasDrawn && !isSignatureValid && (
          <div style={{ 
            marginTop: '12px', 
            padding: '8px 12px', 
            background: '#fef3c7', 
            border: '1px solid #f59e0b', 
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '13px',
            textAlign: 'center'
          }}>
            ⚠️ Signature insufficient. Please continue signing to complete your signature.
          </div>
        )}
      </div>

      {/* Custom Alert Modal */}
      {alertModal.show && (
        <div className="modal-overlay" onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}>
          <div className="modal modal-alert" onClick={(e) => e.stopPropagation()}>
            <div className={`alert-icon alert-icon-${alertModal.type || 'info'}`}>
              {alertModal.type === 'success' && '✓'}
              {alertModal.type === 'error' && '✕'}
              {(!alertModal.type || alertModal.type === 'info') && 'ℹ'}
            </div>
            <div className="alert-message">{alertModal.message}</div>
            <div className="modal-actions">
              <button 
                className={`btn ${alertModal.type === 'success' ? 'btn-success' : alertModal.type === 'error' ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignaturePage;
